import React from 'react';
import { useSelector } from 'react-redux';
import { useRequestData, usePrefs } from 'wsm-common-data';
import PropTypes from 'prop-types';
import { isBrowser } from 'ws-scripts/modules/environment';
import { galleryTrackingEvents } from '../../utilities/tracking';
import useMedia from '../../hooks/useMedia';
import ImageDisplay from './ImageDisplay';
import ImageSkeleton from './ImageSkeleton';
import PlaceholderThumbnail from './PlaceholderThumbnail';
import { MOBILE } from '../../global-constants';
import MyCarsSaveIcon from '../gallery/MyCarsSaveIcon';

const RightHandImage = ({
	onOpenModal,
	setCarouselIndex,
	setActiveToolbar,
	imgTitle
}) => {
	const prefs = usePrefs();
	const { showMyCarsSave, galleryToolbarStyle } = prefs;
	const { imagesToDisplay = [] } = useSelector((state) => state.media);
	const { deviceType, widgetName, windowId } = useRequestData();
	const isDesktop = useMedia('(min-width: 992px)', true);
	const isMobile = deviceType === MOBILE;
	const isThumbOneColumn = !isDesktop || imagesToDisplay.length <= 4;
	const isShowSaveBtn = showMyCarsSave === 'true';
	const isOverlayMode = galleryToolbarStyle === 'none';

	const isSaveStandalone = isShowSaveBtn && !isMobile && !isOverlayMode;

	const image = (index) => {
		// Check if the image at the given index exists
		if (index >= imagesToDisplay.length) {
			return (
				<PlaceholderThumbnail
					key={`placeholder-${index}`}
					keyName="gallery-thumb"
					index={index}
				/>
			);
		}

		return (
			<>
				<ImageDisplay
					image={imagesToDisplay[index]}
					index={index}
					keyName="gallery-thumb"
					onClick={() => {
						onOpenModal();
						setCarouselIndex(index);
						galleryTrackingEvents.openModal(widgetName, windowId, {
							currentItem: index + 1,
							totalItems: imagesToDisplay.length
						});
					}}
					onOpenModal={onOpenModal}
					setActiveToolbar={setActiveToolbar}
					imgTitle={imgTitle}
					isRightHandImage
				/>
			</>
		);
	};

	const mobileContent = !isBrowser ? (
		<>
			<ImageSkeleton />
			<ImageSkeleton />
		</>
	) : (
		<>
			{image(1)}
			{image(2)}
		</>
	);

	const desktopContent = (
		<>
			{image(1)}
			{image(2)}
			{image(3)}
			{!isThumbOneColumn && (
				<>
					{image(4)}
					{image(5)}
					{image(6)}
				</>
			)}
		</>
	);

	if (imagesToDisplay.length > 1) {
		return (
			<div
				data-testid="gallery-right-hand-container"
				className={`right-hand-container position-relative d-flex ${
					!isThumbOneColumn ? 'two-column-thumb' : ''
				} ${isMobile ? 'isMobile' : ''}`}
			>
				<div
					className={`left-column ${
						isThumbOneColumn && !isMobile ? 'w-75' : ''
					}`}
					data-testid="gallery-main-column"
				>
					{image(0)}
				</div>
				<div
					className={`right-column ${
						isThumbOneColumn && !isMobile ? 'w-25' : ''
					}`}
				>
					<div
						className="thumb-column"
						data-testid="gallery-thumb-column"
					>
						{isMobile ? mobileContent : desktopContent}
					</div>
				</div>
				{isSaveStandalone && (
					<div className="gallery-mycars-save-icon absolute-bottom absolute-right mx-4 mb-4">
						<MyCarsSaveIcon />
					</div>
				)}
			</div>
		);
	} else {
		return (
			<div className="position-relative">
				{image(0)}
				{isSaveStandalone && (
					<div className="gallery-mycars-save-icon absolute-bottom absolute-right mx-4 mb-4">
						<MyCarsSaveIcon />
					</div>
				)}
			</div>
		);
	}
};

RightHandImage.propTypes = {
	onOpenModal: PropTypes.func.isRequired,
	setCarouselIndex: PropTypes.func.isRequired,
	setActiveToolbar: PropTypes.func.isRequired,
	imgTitle: PropTypes.string
};

export default RightHandImage;
